/**
 * lib/chunk-load-recovery.ts — stale-deploy auto-recovery after deploys.
 * ("chunk" in the module/hook/storage-key names is historical; the module
 * now covers BOTH stale-build artifact classes.)
 *
 * Every deploy invalidates two kinds of build-hashed references held by an
 * already-open tab:
 *  1. Content-hashed `/_next/static/chunks/*` files — a lazy-load 404s and
 *     the Turbopack runtime throws `ChunkLoadError`.
 *  2. Server Action IDs (build-time content hashes) — invoking a form or
 *     transition action from stale HTML throws `UnrecognizedActionError`
 *     ("Failed to find Server Action …" / "Server Action … was not found on
 *     the server", PostHog issue 019ed08f).
 * In both cases the error boundary renders "Something went wrong" with no
 * way back (reset() re-renders the same tree; it cannot re-fetch a renamed
 * chunk or resolve a renamed action ID). Only a full page reload recovers,
 * because it fetches fresh HTML with current build references.
 *
 * Strategy (PostHog Error Tracking issue 2026-06-12, ChunkLoadError):
 *  - error boundaries: on the FIRST stale-deploy error per cooldown window,
 *    mark sessionStorage and window.location.reload(). The marker prevents
 *    reload loops: if the error recurs right after the reload (a chunk is
 *    persistently missing or an action genuinely gone — a real outage, not
 *    deploy skew), the normal error UI shows and the exception IS reported.
 *  - unhandledrejection: actions invoked from event handlers (and dynamic
 *    imports awaited in handlers) reject OUTSIDE any boundary, so no
 *    error.tsx ever renders — the button silently does nothing and the user
 *    is stuck on the stale build (PostHog issue 019f8e03, /coshh). The
 *    <StaleDeployRejectionRecovery /> listener in the root layout performs
 *    the same guarded reload for these (recoverFromStaleDeployRejection).
 *  - PostHog before_send: drop $exception events for BOTH stale-deploy
 *    classes while no recent reload attempt is recorded (the transient
 *    first hit that auto-heals). Post-reload recurrences pass through and
 *    form Issues, so genuine outages stay visible — see
 *    isSuppressedStaleDeployExceptionEvent.
 */

/** sessionStorage key holding the epoch-ms of the last auto-reload attempt. */
export const CHUNK_RELOAD_FLAG = "tsb-chunk-reload-at";

/**
 * Reload-loop guard window. Within it we will not auto-reload again and we
 * stop suppressing the exception from tracking. Long enough to catch an
 * immediate post-reload recurrence; short enough that a user who keeps a tab
 * open across a LATER deploy gets auto-recovery again.
 */
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Failed to load chunk|Loading chunk [\w-]+ failed|error loading dynamically imported module|Importing a module script failed/i;

/**
 * Stale Server Action after a deploy. Next.js phrases it two ways depending
 * on version/path: "Failed to find Server Action \"<hash>\"" and
 * "Server Action \"<hash>\" was not found on the server". Keep the second
 * predicate anchored on "Server Action" so unrelated not-found messages
 * never match.
 */
const STALE_ACTION_PATTERN =
  /Failed to find Server Action|Server Action "[^"]*" was not found on the server/i;

function errorMessage(error: unknown): string | undefined {
  return typeof error === "string"
    ? error
    : ((error as { message?: unknown } | null | undefined)?.message as
        | string
        | undefined);
}

/** True when the error is a stale/missing JS chunk failure (any bundler phrasing). */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: unknown }).name;
  if (typeof name === "string" && name === "ChunkLoadError") return true;
  const message = errorMessage(error);
  return typeof message === "string" && CHUNK_ERROR_PATTERN.test(message);
}

/** True when the error is a Server Action ID from a previous build (stale tab). */
export function isStaleServerActionError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: unknown }).name;
  if (typeof name === "string" && name === "UnrecognizedActionError") return true;
  const message = errorMessage(error);
  return typeof message === "string" && STALE_ACTION_PATTERN.test(message);
}

/**
 * True for any stale-build artifact error where a full reload (fresh HTML
 * with current chunk references and action IDs) is the only recovery.
 */
export function isStaleDeployError(error: unknown): boolean {
  return isChunkLoadError(error) || isStaleServerActionError(error);
}

function lastReloadAt(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_FLAG);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    // sessionStorage unavailable (privacy mode / iframe) — treat as already
    // attempted so we never risk an unguarded reload loop.
    return Date.now();
  }
}

/** True if an auto-reload was attempted within the cooldown window. */
export function chunkReloadAttemptedRecently(now = Date.now()): boolean {
  const ts = lastReloadAt();
  return ts !== null && now - ts < CHUNK_RELOAD_COOLDOWN_MS;
}

/**
 * Record that an auto-reload is being attempted (call just before reload).
 *
 * Returns whether the guard marker was VERIFIABLY persisted. Callers must
 * only reload when this returns true: if reads work but writes throw or
 * silently no-op (storage quota, private mode, write-blocking policies), an
 * unguarded reload into a persistently missing chunk would loop forever —
 * exactly what the guard exists to prevent (Codex P1, PR #729).
 */
export function markChunkReloadAttempt(now = Date.now()): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(now));
    // Read-back verification — some environments no-op setItem instead of
    // throwing (e.g. Safari private mode historically, quota-exceeded modes).
    return window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === String(now);
  } catch {
    return false;
  }
}

/**
 * Should the error boundary auto-reload for this error? True only for
 * stale-deploy errors (chunk or Server Action) with no recent attempt
 * recorded.
 */
export function shouldAutoReloadForStaleDeployError(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  return isStaleDeployError(error) && !chunkReloadAttemptedRecently();
}

/**
 * Guarded reload for a stale-deploy error that surfaced as an UNHANDLED
 * PROMISE REJECTION — actions invoked from event handlers and dynamic
 * imports awaited in handlers reject outside every error boundary, so the
 * boundary path (useChunkReloadRecovery) never runs for them. Same one-shot
 * semantics as the boundary: reload only when the guard marker verifiably
 * persisted; within the cooldown (or when persistence fails) fall through to
 * nothing — the exception then passes before_send unsuppressed and stays
 * visible in Error Tracking.
 *
 * Returns whether a reload was initiated. `reload` is injectable for tests
 * only (jsdom cannot stub window.location.reload).
 */
export function recoverFromStaleDeployRejection(
  reason: unknown,
  reload: () => void = () => window.location.reload(),
): boolean {
  if (!shouldAutoReloadForStaleDeployError(reason)) return false;
  if (!markChunkReloadAttempt()) return false;
  reload();
  return true;
}

/**
 * Capability probe: can the reload loop-guard actually persist? Mirrors the
 * verified-write semantics of markChunkReloadAttempt() without consuming the
 * real marker. In environments where reads work but writes throw or silently
 * no-op, auto-recovery is impossible (the hook falls back to the error UI) —
 * so chunk exceptions there must NOT be suppressed from Error Tracking
 * (Codex P2 #2, PR #729).
 */
function canPersistReloadGuard(): boolean {
  if (typeof window === "undefined") return false;
  const probe = `${CHUNK_RELOAD_FLAG}-probe`;
  try {
    window.sessionStorage.setItem(probe, "1");
    const ok = window.sessionStorage.getItem(probe) === "1";
    window.sessionStorage.removeItem(probe);
    return ok;
  } catch {
    return false;
  }
}

/** Minimal shape of a posthog-js CaptureResult we need for filtering. */
export interface CaptureLikeEvent {
  event?: string;
  properties?: {
    $exception_list?: Array<{ type?: string; value?: string }>;
  } & Record<string, unknown>;
}

/**
 * PostHog `before_send` predicate: drop transient (pre-reload) stale-deploy
 * exception events — stale chunks AND stale Server Actions — so every deploy
 * doesn't spray a fresh Issue (action IDs are per-build hashes, so each skew
 * hit mints a new fingerprint and a new alert). NOT dropped: post-reload
 * recurrences (guard flag set), and environments where the guard cannot
 * persist at all (auto-recovery impossible there, so the failure must stay
 * visible in Error Tracking).
 *
 * History: PR #1439 deliberately did NOT suppress stale Server Action errors
 * because handler-invoked actions rejected outside any boundary and were
 * never auto-reloaded — suppressing them would have hidden stuck users. That
 * premise no longer holds: <StaleDeployRejectionRecovery /> (root layout)
 * now performs the same guarded reload for unhandled rejections, so the two
 * classes are treated symmetrically. The listener writes the guard marker in
 * a NEW task, strictly after autocapture's synchronous capture/before_send
 * pass over the same rejection, so the transient event is evaluated against
 * "no attempt yet" and dropped regardless of listener registration order.
 */
export function isSuppressedStaleDeployExceptionEvent(
  event: CaptureLikeEvent | null | undefined,
): boolean {
  if (!event || event.event !== "$exception") return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list)) return false;
  const isStaleDeploy = list.some(
    (e) =>
      e?.type === "ChunkLoadError" ||
      e?.type === "UnrecognizedActionError" ||
      (typeof e?.value === "string" &&
        (CHUNK_ERROR_PATTERN.test(e.value) ||
          STALE_ACTION_PATTERN.test(e.value))),
  );
  return (
    isStaleDeploy && !chunkReloadAttemptedRecently() && canPersistReloadGuard()
  );
}
